import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavStep } from '../lib/NavHistory';
import {
  Shield, Users, Search, ChevronDown, ChevronUp, Check, X, Minus,
  RefreshCw, Clock, AlertTriangle, Lock, Unlock, Loader2, Filter,
  UserPlus, Calendar, XCircle, ArrowRight,
} from 'lucide-react';

// ── Permission category labels ──────────────────────────────────────────────
const CATEGORY_LABELS = {
  patients: 'Patient Records (PHI)',
  encounters: 'Clinical Encounters',
  medications: 'Medications',
  labs: 'Lab Results',
  vitals: 'Vital Signs',
  allergies: 'Allergies',
  audit: 'Audit Log',
  users: 'User Management',
  settings: 'System Settings',
  research: 'Research',
  dashboard: 'Dashboard',
  chat: 'Secure Chat',
  email: 'Secure Email',
  ai_research: 'AI Research',
  bi: 'Business Intelligence',
  billing: 'Billing',
  crm: 'CRM',
  admin_ai: 'Admin AI',
  scheduling: 'Scheduling',
  notifications: 'Notifications',
  export: 'Data Export',
  totp: 'Two-Factor Auth',
  portal: 'Patient Portal',
  reports: 'Report Builder',
  alerts: 'AI Alerts',
  telemed: 'Telemedicine',
  insurance: 'Insurance',
  personnel: 'Personnel',
  leave: 'Leave Requests',
  biolab_docs: 'Biolab Documents',
  clinical_ai: 'Clinical AI',
  crm_ai: 'CRM AI',
  billing_ai: 'Billing AI',
  scheduling_ai: 'Scheduling AI',
};

const OVERRIDE_COLORS = {
  grant: { bg: '#f0fdf4', border: '#86efac', text: '#166534', badge: '#16a34a' },
  deny: { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', badge: '#dc2626' },
  role: { bg: '#f9fafb', border: '#e5e7eb', text: '#6b7280', badge: '#9ca3af' },
};

export default function PermissionManagerPage({ user: currentUser, onNavigate }) {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [allPermissions, setAllPermissions] = useState({});
  const [grouped, setGrouped] = useState({});
  const [roleDefaults, setRoleDefaults] = useState([]);
  const [effective, setEffective] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [history, setHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [permSearch, setPermSearch] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // all | overrides | granted | denied
  const [expandedCats, setExpandedCats] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [reason, setReason] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [activeTab, setActiveTab] = useState('permissions');

  // ── Delegation state ─────────────────────────────────────────────────────
  const [delegations, setDelegations] = useState([]);
  const [delegForm, setDelegForm] = useState({ delegateId: '', permissions: [], reason: '', durationDays: 30 });
  const [delegExpanded, setDelegExpanded] = useState(new Set());
  const [delegSaving, setDelegSaving] = useState(false);
  const [showNewDeleg, setShowNewDeleg] = useState(false);
  const [confirmingRevoke, setConfirmingRevoke] = useState(null);
  const [confirmingClearAll, setConfirmingClearAll] = useState(false);
  const [delegPermSearch, setDelegPermSearch] = useState('');

  const api = window.zignagenixAPI;
  const rightPanelRef = useRef(null);

  // #65 Phase B: register the drill-down + modals as Back steps (called
  // unconditionally, above any early return). Labels are GENERIC — never the
  // selected user's name/id. Back closes the deepest one first (user detail,
  // then modals, then the tab, then the route).
  useNavStep({ active: !!selectedUser, label: 'User detail', onBack: () => setSelectedUser(null) });
  useNavStep({ active: showHistory, label: 'Permission history', onBack: () => setShowHistory(false) });
  useNavStep({ active: showNewDeleg, label: 'New delegation', onBack: () => setShowNewDeleg(false) });

  // ── Load users ─────────────────────────────────────────────────────────────
  useEffect(() => {
    loadUsers();
    loadDefinitions();
    // Check for expired delegations on mount
    if (api.checkDelegationExpiry) api.checkDelegationExpiry().catch(() => {});
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await api.listUsers();
      if (Array.isArray(res)) setUsers(res);
    } catch (err) { console.error('[Perms] User load failed:', err); }
  }, [api]);

  const loadDefinitions = useCallback(async () => {
    try {
      const res = await api.getPermissionDefinitions();
      if (res?.success) {
        setAllPermissions(res.permissions || {});
        setGrouped(res.grouped || {});
      }
    } catch (err) { console.error('[Perms] Definitions load failed:', err); }
  }, [api]);

  // ── Select a user ──────────────────────────────────────────────────────────
  const selectUser = useCallback(async (userId) => {
    setLoading(true);
    setSelectedUser(null);
    try {
      const [effRes, ovRes] = await Promise.all([
        api.getUserEffectivePermissions(userId),
        api.getUserPermissionOverrides(userId),
      ]);
      if (effRes?.success) {
        setSelectedUser(effRes.user);
        setRoleDefaults(effRes.roleDefaults || []);
        setEffective(effRes.effective || []);
      }
      if (ovRes?.success) {
        setOverrides(ovRes.overrides || []);
      }
      // Expand all categories by default
      setExpandedCats(new Set(Object.keys(grouped)));
    } catch (err) { console.error('[Perms] User load failed:', err); }
    setLoading(false);
  }, [api, grouped]);

  // ── Refresh permissions in-place (no scroll reset) ────────────────────────
  const refreshPermissions = useCallback(async (userId) => {
    // Save scroll position before updating state
    const scrollTop = rightPanelRef.current?.scrollTop || 0;
    try {
      const [effRes, ovRes] = await Promise.all([
        api.getUserEffectivePermissions(userId),
        api.getUserPermissionOverrides(userId),
      ]);
      if (effRes?.success) {
        setSelectedUser(effRes.user);
        setRoleDefaults(effRes.roleDefaults || []);
        setEffective(effRes.effective || []);
      }
      if (ovRes?.success) {
        setOverrides(ovRes.overrides || []);
      }
      // Restore scroll position after React renders
      requestAnimationFrame(() => {
        if (rightPanelRef.current) {
          rightPanelRef.current.scrollTop = scrollTop;
        }
      });
    } catch (err) { console.error('[Perms] Refresh failed:', err); }
  }, [api]);

  // ── Set override ───────────────────────────────────────────────────────────
  const setOverride = useCallback(async (permission, overrideType) => {
    if (!selectedUser) return;
    setSaving(permission);
    try {
      const res = await api.setPermissionOverride(selectedUser.id, permission, overrideType, reason || undefined);
      if (res?.success) {
        await refreshPermissions(selectedUser.id);
      }
    } catch (err) { console.error('[Perms] Set override failed:', err); }
    setSaving(null);
  }, [api, selectedUser, reason, refreshPermissions]);

  // ── Remove override ────────────────────────────────────────────────────────
  const removeOverride = useCallback(async (permission) => {
    if (!selectedUser) return;
    setSaving(permission);
    try {
      const res = await api.removePermissionOverride(selectedUser.id, permission);
      if (res?.success) {
        await refreshPermissions(selectedUser.id);
      }
    } catch (err) { console.error('[Perms] Remove override failed:', err); }
    setSaving(null);
  }, [api, selectedUser, refreshPermissions]);

  // ── Clear all overrides ────────────────────────────────────────────────────
  const clearAll = useCallback(async () => {
    if (!selectedUser) return;
    setSaving('__clear_all__');
    try {
      await api.clearUserPermissionOverrides(selectedUser.id);
      setConfirmingClearAll(false);
      await refreshPermissions(selectedUser.id);
    } catch (err) { setConfirmingClearAll(false); console.error('[Perms] Clear failed:', err); }
    setSaving(null);
  }, [api, selectedUser, refreshPermissions]);

  // ── Load audit history ─────────────────────────────────────────────────────
  const loadHistory = useCallback(async () => {
    try {
      const res = await api.getPermissionAuditTrail({ userId: selectedUser?.id, limit: 100 });
      if (res?.success) setHistory(res.history || []);
    } catch (err) { console.error('[Perms] History load failed:', err); }
  }, [api, selectedUser]);

  useEffect(() => { if (showHistory) loadHistory(); }, [showHistory, loadHistory]);

  // ── Delegation functions ──────────────────────────────────────────────────
  const loadDelegations = useCallback(async () => {
    try {
      const res = await api.listDelegations({});
      if (res?.success) setDelegations(res.delegations || []);
    } catch (err) { console.error('[Deleg] Load failed:', err); }
  }, [api]);

  const createDelegation = useCallback(async (overrideDelegateId) => {
    const targetId = overrideDelegateId || delegForm.delegateId;
    if (!targetId || delegForm.permissions.length === 0) return;
    setDelegSaving(true);
    try {
      const res = await api.createDelegation({
        delegateId: parseInt(targetId, 10),
        permissions: delegForm.permissions,
        reason: delegForm.reason || undefined,
        durationDays: parseInt(delegForm.durationDays, 10) || 30,
      });
      if (res?.success) {
        setDelegForm({ delegateId: '', permissions: [], reason: '', durationDays: 30 });
        setShowNewDeleg(false);
        await loadDelegations();
      } else {
        alert(res?.message || 'Failed to create delegation');
      }
    } catch (err) { console.error('[Deleg] Create failed:', err); alert('Delegation creation failed'); }
    setDelegSaving(false);
  }, [api, delegForm, loadDelegations]);

  const revokeDelegation = useCallback(async (delegationId) => {
    try {
      const res = await api.revokeDelegation(delegationId);
      if (res?.success) { setConfirmingRevoke(null); await loadDelegations(); }
      else { setConfirmingRevoke(null); console.error('Revoke failed:', res?.message); }
    } catch (err) { setConfirmingRevoke(null); console.error('[Deleg] Revoke failed:', err); }
  }, [api, loadDelegations]);

  const toggleDelegPerm = (perm) => {
    setDelegForm(prev => {
      const perms = prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm];
      return { ...prev, permissions: perms };
    });
  };

  const selectAllPermsInCategory = (catPerms) => {
    setDelegForm(prev => {
      const allSelected = catPerms.every(p => prev.permissions.includes(p.permission));
      const newPerms = allSelected
        ? prev.permissions.filter(p => !catPerms.some(cp => cp.permission === p))
        : [...new Set([...prev.permissions, ...catPerms.map(p => p.permission)])];
      return { ...prev, permissions: newPerms };
    });
  };

  const selectAllPermissions = () => {
    setDelegForm(prev => {
      const allPerms = Object.values(grouped).flat().map(p => p.permission);
      const allSelected = allPerms.length === prev.permissions.length;
      return { ...prev, permissions: allSelected ? [] : allPerms };
    });
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const overrideMap = {};
  for (const ov of overrides) {
    overrideMap[ov.permission] = ov.override_type;
  }

  function getPermStatus(perm) {
    if (overrideMap[perm] === 'grant') return 'granted';
    if (overrideMap[perm] === 'deny') return 'denied';
    if (roleDefaults.includes(perm)) return 'role';
    return 'none';
  }

  function toggleCategory(cat) {
    setExpandedCats(prev => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  }

  // Filter users by search, then sort A→Z by display name (display order only;
  // the API returns them in id/insertion order). Locale-aware, case-insensitive.
  const filteredUsers = users.filter(u =>
    (u.display_name || u.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.role || '').toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) =>
    (a.display_name || a.username || '').localeCompare(b.display_name || b.username || '', undefined, { sensitivity: 'base' })
  );

  // Filter permissions by search and mode
  function filterPerms(perms) {
    let result = perms;
    if (permSearch) {
      const s = permSearch.toLowerCase();
      result = result.filter(p => p.permission.toLowerCase().includes(s) || p.description.toLowerCase().includes(s));
    }
    if (filterMode === 'overrides') {
      result = result.filter(p => overrideMap[p.permission]);
    } else if (filterMode === 'granted') {
      result = result.filter(p => effective.includes(p.permission));
    } else if (filterMode === 'denied') {
      result = result.filter(p => !effective.includes(p.permission));
    }
    // Rows A→Z by permission key (display order only; filters applied above).
    return result.slice().sort((a, b) =>
      a.permission.localeCompare(b.permission, undefined, { sensitivity: 'base' }));
  }

  // Category groups A→Z by their DISPLAYED label (what the admin reads — e.g.
  // "AI Alerts" before "Business Intelligence"), display order only. Keys/
  // membership unchanged, so Expand/Collapse-all over Object.keys(grouped) and
  // the filter chips still operate on the full set.
  const sortedGroupedEntries = Object.entries(grouped).sort(([a], [b]) =>
    (CATEGORY_LABELS[a] || a).localeCompare(CATEGORY_LABELS[b] || b, undefined, { sensitivity: 'base' }));

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)', overflow: 'hidden' }}>
      {/* ── Left Panel: User List ──────────────────────────────────────────── */}
      <div style={{
        width: 300, borderRight: '1px solid #e5e7eb', display: 'flex',
        flexDirection: 'column', background: '#f9fafb',
      }}>
        <div style={{ padding: '20px 16px 12px' }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield size={20} color="#2E8B98" /> Permission Manager
          </h2>
          <div style={{ position: 'relative' }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: 10, color: '#9ca3af' }} />
            <input
              type="text" placeholder="Search users…" value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                width: '100%', padding: '8px 12px 8px 32px', borderRadius: 8,
                border: '1px solid #d1d5db', fontSize: 13, boxSizing: 'border-box',
              }}
            />
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '0 8px 8px' }}>
          {filteredUsers.map(u => {
            const isSelected = selectedUser?.id === u.id;
            return (
              <button key={u.id} onClick={() => selectUser(u.id)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: isSelected ? '#2E8B98' : 'transparent',
                  color: isSelected ? '#fff' : '#374151', textAlign: 'left',
                  marginBottom: 2, transition: 'all 0.1s',
                }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                  background: isSelected ? 'rgba(255,255,255,0.2)' : '#e5e7eb',
                  color: isSelected ? '#fff' : '#6b7280',
                }}>
                  {(u.display_name || '??').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.display_name || u.username}
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.7 }}>{u.role}{!u.is_active ? ' (inactive)' : ''}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Right Panel: Permission Editor ─────────────────────────────────── */}
      <div ref={rightPanelRef} style={{ flex: 1, overflow: 'auto', padding: '24px 32px' }}>
        {!selectedUser && !loading && (
          <div style={{ textAlign: 'center', padding: '80px 20px', color: '#9ca3af' }}>
            <Users size={48} style={{ marginBottom: 12 }} />
            <p style={{ fontSize: 16 }}>Select a user from the left to manage their permissions.</p>
            <p style={{ fontSize: 13 }}>You can grant or deny individual permissions beyond the role defaults.</p>
          </div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '80px 20px', color: '#9ca3af' }}>
            <Loader2 size={32} className="spin" />
            <p>Loading permissions…</p>
          </div>
        )}

        {selectedUser && !loading && (
          <>
            {/* ── User Header ──────────────────────────────────────────────── */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 20, flexWrap: 'wrap', gap: 12,
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{selectedUser.displayName}</h2>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                  Role: <strong>{selectedUser.role}</strong> · {effective.length} / {Object.keys(allPermissions).length} permissions active
                  {overrides.length > 0 && (
                    <span style={{ marginLeft: 8, color: '#d97706', fontWeight: 600 }}>
                      ({overrides.length} custom override{overrides.length !== 1 ? 's' : ''})
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setShowHistory(h => !h); setActiveTab(showHistory ? 'permissions' : 'history'); }}
                  style={btnStyle('#6b7280')}>
                  <Clock size={14} /> History
                </button>
                {overrides.length > 0 && !confirmingClearAll && (
                  <button onClick={() => setConfirmingClearAll(true)} style={btnStyle('#dc2626')}>
                    <X size={14} /> Clear All Overrides
                  </button>
                )}
                {confirmingClearAll && (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>Clear ALL overrides?</span>
                    <button onClick={clearAll}
                      style={{ ...btnStyle('#dc2626'), fontSize: 11 }}>
                      Yes, Clear All
                    </button>
                    <button onClick={() => setConfirmingClearAll(false)}
                      style={{ ...btnStyle('#6b7280'), fontSize: 11 }}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* ── Reason Input ──────────────────────────────────────────────── */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
              padding: '10px 14px', background: '#f0f9ff', borderRadius: 10,
              border: '1px solid #bae6fd',
            }}>
              <AlertTriangle size={16} color="#0369a1" />
              <span style={{ fontSize: 12, color: '#0369a1', fontWeight: 500 }}>HIPAA audit reason:</span>
              <input type="text" placeholder="Reason for permission changes (optional but recommended)"
                value={reason} onChange={e => setReason(e.target.value)}
                style={{
                  flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #bae6fd',
                  fontSize: 13, background: '#fff',
                }}
              />
            </div>

            {/* ── Tabs ─────────────────────────────────────────────────────── */}
            <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e5e7eb', marginBottom: 16 }}>
              {[
                { id: 'permissions', label: 'Permissions' },
                { id: 'delegations', label: 'Delegations' },
                { id: 'history', label: 'Change History' },
              ].map(tab => (
                <button key={tab.id} onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'history') setShowHistory(true);
                  if (tab.id === 'delegations') loadDelegations();
                }}
                  style={{
                    padding: '8px 20px', fontSize: 13, fontWeight: 600, border: 'none',
                    cursor: 'pointer', background: 'transparent',
                    color: activeTab === tab.id ? '#2E8B98' : '#6b7280',
                    borderBottom: activeTab === tab.id ? '2px solid #2E8B98' : '2px solid transparent',
                    marginBottom: -2,
                  }}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ── Permissions Tab ───────────────────────────────────────────── */}
            {activeTab === 'permissions' && (
              <>
                {/* Filter Bar */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ position: 'relative', flex: '1 1 240px' }}>
                    <Search size={14} style={{ position: 'absolute', left: 10, top: 9, color: '#9ca3af' }} />
                    <input type="text" placeholder="Filter permissions…" value={permSearch}
                      onChange={e => setPermSearch(e.target.value)}
                      style={{
                        width: '100%', padding: '7px 12px 7px 30px', borderRadius: 8,
                        border: '1px solid #d1d5db', fontSize: 13, boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[
                      { id: 'all', label: 'All' },
                      { id: 'overrides', label: 'Overrides Only' },
                      { id: 'granted', label: 'Active' },
                      { id: 'denied', label: 'Denied' },
                    ].map(f => (
                      <button key={f.id} onClick={() => setFilterMode(f.id)}
                        style={{
                          padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                          border: '1px solid', cursor: 'pointer',
                          background: filterMode === f.id ? '#2E8B98' : '#fff',
                          color: filterMode === f.id ? '#fff' : '#6b7280',
                          borderColor: filterMode === f.id ? '#2E8B98' : '#d1d5db',
                        }}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                  {/* Expand-all / collapse-all PAIR — operate on every category
                      group at once. Individual category toggles still work. */}
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => setExpandedCats(new Set(Object.keys(grouped)))}
                      style={{ ...btnStyle('#6b7280'), padding: '6px 12px', fontSize: 12 }}>
                      Expand all
                    </button>
                    <button onClick={() => setExpandedCats(new Set())}
                      style={{ ...btnStyle('#6b7280'), padding: '6px 12px', fontSize: 12 }}>
                      Collapse all
                    </button>
                  </div>
                </div>

                {/* Permission Categories (sorted A→Z by displayed label) */}
                {sortedGroupedEntries.map(([category, perms]) => {
                  const filtered = filterPerms(perms);
                  if (filtered.length === 0) return null;
                  const isExpanded = expandedCats.has(category);
                  const catOverrides = filtered.filter(p => overrideMap[p.permission]);
                  const catActive = filtered.filter(p => effective.includes(p.permission));

                  return (
                    <div key={category} style={{
                      border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 8, overflow: 'hidden',
                    }}>
                      {/* Category Header */}
                      <div onClick={() => toggleCategory(category)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '10px 14px', cursor: 'pointer', background: '#f9fafb',
                          borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none',
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {isExpanded ? <ChevronUp size={16} color="#9ca3af" /> : <ChevronDown size={16} color="#9ca3af" />}
                          <strong style={{ fontSize: 13, color: '#374151' }}>
                            {CATEGORY_LABELS[category] || category}
                          </strong>
                          <span style={{ fontSize: 11, color: '#9ca3af' }}>
                            {catActive.length}/{filtered.length} active
                          </span>
                          {catOverrides.length > 0 && (
                            <span style={{
                              fontSize: 10, fontWeight: 700, color: '#d97706',
                              background: '#fffbeb', padding: '1px 6px', borderRadius: 6,
                            }}>
                              {catOverrides.length} override{catOverrides.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Permission Rows */}
                      {isExpanded && filtered.map(p => {
                        const status = getPermStatus(p.permission);
                        const isActive = effective.includes(p.permission);
                        const hasOverride = !!overrideMap[p.permission];
                        const isSaving = saving === p.permission;

                        return (
                          <div key={p.permission} style={{
                            display: 'flex', alignItems: 'center', padding: '8px 14px',
                            borderBottom: '1px solid #f3f4f6', gap: 10,
                            background: hasOverride
                              ? (overrideMap[p.permission] === 'grant' ? '#f0fdf410' : '#fef2f210')
                              : '#fff',
                          }}>
                            {/* Status icon */}
                            <div style={{ width: 24, display: 'flex', justifyContent: 'center' }}>
                              {isActive ? <Check size={16} color="#16a34a" /> : <X size={16} color="#dc2626" />}
                            </div>

                            {/* Permission info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>
                                <code style={{ fontSize: 12, background: '#f3f4f6', padding: '1px 6px', borderRadius: 4 }}>
                                  {p.permission}
                                </code>
                                {hasOverride && (
                                  <span style={{
                                    marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '1px 6px',
                                    borderRadius: 4,
                                    background: OVERRIDE_COLORS[overrideMap[p.permission]]?.bg,
                                    color: OVERRIDE_COLORS[overrideMap[p.permission]]?.text,
                                    border: `1px solid ${OVERRIDE_COLORS[overrideMap[p.permission]]?.border}`,
                                  }}>
                                    {overrideMap[p.permission] === 'grant' ? '✚ GRANTED' : '⊘ DENIED'}
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{p.description}</div>
                            </div>

                            {/* Action buttons */}
                            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                              {isSaving ? (
                                <Loader2 size={16} className="spin" color="#9ca3af" />
                              ) : (
                                <>
                                  <button onClick={() => setOverride(p.permission, 'grant')}
                                    title="Grant this permission"
                                    disabled={overrideMap[p.permission] === 'grant'}
                                    style={actionBtn('#16a34a', overrideMap[p.permission] === 'grant')}>
                                    <Unlock size={13} />
                                  </button>
                                  <button onClick={() => setOverride(p.permission, 'deny')}
                                    title="Deny this permission"
                                    disabled={overrideMap[p.permission] === 'deny'}
                                    style={actionBtn('#dc2626', overrideMap[p.permission] === 'deny')}>
                                    <Lock size={13} />
                                  </button>
                                  {hasOverride && (
                                    <button onClick={() => removeOverride(p.permission)}
                                      title="Revert to role default"
                                      style={actionBtn('#6b7280', false)}>
                                      <Minus size={13} />
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </>
            )}

            {/* ── Delegations Tab ─────────────────────────────────────────── */}
            {activeTab === 'delegations' && (
              <div>
                {/* New Delegation Button */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>
                    Delegate selected permissions to {selectedUser.displayName} for a set period (up to 12 months).
                  </div>
                  <button onClick={() => setShowNewDeleg(!showNewDeleg)}
                    style={btnStyle(showNewDeleg ? '#6b7280' : '#2E8B98')}>
                    {showNewDeleg ? <><X size={14} /> Cancel</> : <><UserPlus size={14} /> New Delegation</>}
                  </button>
                </div>

                {/* New Delegation Form */}
                {showNewDeleg && (
                  <div style={{
                    border: '1px solid #bae6fd', borderRadius: 10, padding: 20,
                    background: '#f0f9ff', marginBottom: 20,
                  }}>
                    <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#0369a1' }}>
                      <UserPlus size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                      Create Delegation for {selectedUser.displayName}
                    </h3>

                    {/* Duration */}
                    <div style={{ display: 'flex', gap: 16, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                      <div>
                        <label style={labelStyle}>Duration (days)</label>
                        <input type="number" min="1" max="365"
                          value={delegForm.durationDays}
                          onChange={e => setDelegForm(prev => ({ ...prev, durationDays: e.target.value }))}
                          style={{ ...inputStyle, width: 100 }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>Reason (HIPAA audit)</label>
                        <input type="text" placeholder="Why is this delegation needed?"
                          value={delegForm.reason}
                          onChange={e => setDelegForm(prev => ({ ...prev, reason: e.target.value }))}
                          style={{ ...inputStyle, width: '100%' }}
                        />
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', alignSelf: 'flex-end', paddingBottom: 4 }}>
                        <Calendar size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                        Expires: {new Date(Date.now() + (parseInt(delegForm.durationDays, 10) || 30) * 86400000).toLocaleDateString()}
                      </div>
                    </div>

                    {/* Permission Selector */}
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <label style={{ ...labelStyle, margin: 0 }}>
                          Select Permissions ({delegForm.permissions.length} selected)
                        </label>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={selectAllPermissions}
                            style={{ ...btnSmall, background: '#e5e7eb', color: '#374151' }}>
                            {delegForm.permissions.length === Object.values(grouped).flat().length ? 'Deselect All' : 'Select All'}
                          </button>
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <Search size={13} style={{ position: 'absolute', left: 8, top: 6, color: '#9ca3af' }} />
                            <input type="text" placeholder="Filter…" value={delegPermSearch}
                              onChange={e => setDelegPermSearch(e.target.value)}
                              style={{ ...inputStyle, paddingLeft: 26, width: 160, fontSize: 11 }}
                            />
                          </div>
                        </div>
                      </div>
                      <div style={{
                        maxHeight: 280, overflow: 'auto', border: '1px solid #d1d5db',
                        borderRadius: 8, background: '#fff',
                      }}>
                        {Object.entries(grouped).map(([cat, perms]) => {
                          const filtered = delegPermSearch
                            ? perms.filter(p => p.permission.toLowerCase().includes(delegPermSearch.toLowerCase()) || p.description.toLowerCase().includes(delegPermSearch.toLowerCase()))
                            : perms;
                          if (filtered.length === 0) return null;
                          const isExpanded = delegExpanded.has(cat);
                          const selectedCount = filtered.filter(p => delegForm.permissions.includes(p.permission)).length;
                          return (
                            <div key={cat}>
                              <div onClick={() => setDelegExpanded(prev => {
                                const next = new Set(prev); next.has(cat) ? next.delete(cat) : next.add(cat); return next;
                              })}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                                  cursor: 'pointer', background: '#f9fafb', borderBottom: '1px solid #f3f4f6',
                                  fontSize: 12, fontWeight: 600, color: '#374151',
                                }}>
                                {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                {CATEGORY_LABELS[cat] || cat}
                                <span style={{ color: '#9ca3af', fontWeight: 400 }}>
                                  {selectedCount}/{filtered.length}
                                </span>
                                <button onClick={e => { e.stopPropagation(); selectAllPermsInCategory(filtered); }}
                                  style={{ ...btnSmall, marginLeft: 'auto', fontSize: 10 }}>
                                  {selectedCount === filtered.length ? 'None' : 'All'}
                                </button>
                              </div>
                              {isExpanded && filtered.map(p => (
                                <label key={p.permission} style={{
                                  display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px 4px 28px',
                                  borderBottom: '1px solid #f9fafb', fontSize: 12, cursor: 'pointer',
                                  background: delegForm.permissions.includes(p.permission) ? '#f0fdf420' : '#fff',
                                }}>
                                  <input type="checkbox"
                                    checked={delegForm.permissions.includes(p.permission)}
                                    onChange={() => toggleDelegPerm(p.permission)}
                                  />
                                  <code style={{ fontSize: 11, background: '#f3f4f6', padding: '0 4px', borderRadius: 3 }}>
                                    {p.permission}
                                  </code>
                                  <span style={{ color: '#9ca3af', fontSize: 11 }}>{p.description}</span>
                                </label>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Create Button */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      <button onClick={() => setShowNewDeleg(false)}
                        style={{ ...btnStyle('#6b7280'), opacity: 0.8 }}>Cancel</button>
                      <button onClick={() => createDelegation(selectedUser.id)}
                        disabled={delegForm.permissions.length === 0 || delegSaving}
                        style={{
                          ...btnStyle('#16a34a'),
                          opacity: delegForm.permissions.length === 0 ? 0.5 : 1,
                          cursor: delegForm.permissions.length === 0 ? 'not-allowed' : 'pointer',
                        }}>
                        {delegSaving ? <><Loader2 size={14} className="spin" /> Creating…</> : <><Check size={14} /> Create Delegation</>}
                      </button>
                    </div>
                  </div>
                )}

                {/* Existing Delegations for this user */}
                {(() => {
                  const userDelegations = delegations.filter(d => d.delegate_id === selectedUser.id);
                  if (userDelegations.length === 0 && !showNewDeleg) {
                    return (
                      <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af' }}>
                        <UserPlus size={40} style={{ marginBottom: 10, opacity: 0.5 }} />
                        <p style={{ fontSize: 14 }}>No delegations for {selectedUser.displayName}.</p>
                        <p style={{ fontSize: 12 }}>Use "New Delegation" to temporarily grant this user additional permissions.</p>
                      </div>
                    );
                  }
                  return userDelegations.map(d => {
                    const isActive = d.status === 'active';
                    const isScheduled = d.status === 'scheduled';
                    const statusColor = isActive ? '#16a34a' : isScheduled ? '#d97706' : '#9ca3af';
                    const statusBg = isActive ? '#f0fdf4' : isScheduled ? '#fffbeb' : '#f9fafb';
                    const daysLeft = isActive ? Math.ceil((new Date(d.expires_at) - new Date()) / 86400000) : 0;
                    return (
                      <div key={d.id} style={{
                        border: `1px solid ${isActive ? '#86efac' : '#e5e7eb'}`,
                        borderRadius: 10, padding: 16, marginBottom: 10,
                        background: isActive ? '#fafffe' : '#fafafa',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{
                              fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 6,
                              background: statusBg, color: statusColor, border: `1px solid ${statusColor}30`,
                              textTransform: 'uppercase',
                            }}>
                              {d.status}
                            </span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                              {(d.parsedPermissions || []).length} permissions
                            </span>
                            {isActive && (
                              <span style={{ fontSize: 12, color: daysLeft <= 7 ? '#dc2626' : '#6b7280' }}>
                                ({daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining)
                              </span>
                            )}
                          </div>
                          {(isActive || isScheduled) && confirmingRevoke !== d.id && (
                            <button onClick={() => setConfirmingRevoke(d.id)}
                              style={btnStyle('#dc2626')}>
                              <XCircle size={13} /> Revoke
                            </button>
                          )}
                          {confirmingRevoke === d.id && (
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <span style={{ fontSize: 11, color: '#dc2626' }}>Confirm?</span>
                              <button onClick={() => revokeDelegation(d.id)}
                                style={{ ...btnStyle('#dc2626'), fontSize: 11 }}>
                                Yes, Revoke
                              </button>
                              <button onClick={() => setConfirmingRevoke(null)}
                                style={{ ...btnStyle('#6b7280'), fontSize: 11 }}>
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'flex', gap: 20, fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                          <span><strong>Delegated by:</strong> {d.delegated_by_name || '—'}</span>
                          <span><strong>From:</strong> {new Date(d.starts_at).toLocaleDateString()}</span>
                          <span><strong>Until:</strong> {new Date(d.expires_at).toLocaleDateString()}</span>
                          {d.revoked_at && <span><strong>Revoked:</strong> {new Date(d.revoked_at).toLocaleDateString()} by {d.revoked_by_name || '—'}</span>}
                        </div>
                        {d.reason && (
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                            <strong>Reason:</strong> {d.reason}
                          </div>
                        )}

                        {/* Permission chips */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {(d.parsedPermissions || []).slice(0, 20).map(p => (
                            <code key={p} style={{
                              fontSize: 10, padding: '1px 6px', borderRadius: 4,
                              background: isActive ? '#f0fdf4' : '#f3f4f6',
                              color: isActive ? '#166534' : '#6b7280',
                              border: `1px solid ${isActive ? '#bbf7d0' : '#e5e7eb'}`,
                            }}>
                              {p}
                            </code>
                          ))}
                          {(d.parsedPermissions || []).length > 20 && (
                            <span style={{ fontSize: 10, color: '#9ca3af', padding: '2px 4px' }}>
                              +{d.parsedPermissions.length - 20} more
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}

                {/* All Delegations (global view) */}
                {(() => {
                  const otherDelegations = delegations.filter(d => d.delegate_id !== selectedUser.id && d.status === 'active');
                  if (otherDelegations.length === 0) return null;
                  return (
                    <div style={{ marginTop: 24 }}>
                      <h4 style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 10 }}>
                        Other Active Delegations
                      </h4>
                      {otherDelegations.map(d => (
                        <div key={d.id} style={{
                          border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px',
                          marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          background: '#f9fafb', fontSize: 12,
                        }}>
                          <div>
                            <strong>{d.delegate_name || d.delegate_username}</strong>
                            <span style={{ color: '#9ca3af', marginLeft: 6 }}>({d.delegate_role})</span>
                            <ArrowRight size={12} style={{ margin: '0 6px', color: '#9ca3af', verticalAlign: 'middle' }} />
                            <span>{(d.parsedPermissions || []).length} permissions</span>
                            <span style={{ marginLeft: 8, color: '#6b7280' }}>until {new Date(d.expires_at).toLocaleDateString()}</span>
                          </div>
                          {confirmingRevoke !== d.id ? (
                            <button onClick={() => setConfirmingRevoke(d.id)}
                              style={{ ...btnSmall, color: '#dc2626', borderColor: '#fca5a5' }}>
                              Revoke
                            </button>
                          ) : (
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                              <button onClick={() => revokeDelegation(d.id)}
                                style={{ ...btnSmall, color: '#fff', background: '#dc2626', borderColor: '#dc2626' }}>
                                Confirm
                              </button>
                              <button onClick={() => setConfirmingRevoke(null)}
                                style={{ ...btnSmall, color: '#6b7280', borderColor: '#d1d5db' }}>
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── History Tab ──────────────────────────────────────────────── */}
            {activeTab === 'history' && (
              <div>
                {history.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                    <Clock size={36} style={{ marginBottom: 8 }} />
                    <p>No permission changes recorded yet.</p>
                  </div>
                )}
                {history.length > 0 && (
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f9fafb' }}>
                          <th style={thStyle}>Date</th>
                          <th style={thStyle}>User</th>
                          <th style={thStyle}>Permission</th>
                          <th style={thStyle}>Action</th>
                          <th style={thStyle}>Change</th>
                          <th style={thStyle}>Changed By</th>
                          <th style={thStyle}>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((h, i) => (
                          <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                            <td style={tdStyle}>{new Date(h.created_at).toLocaleString()}</td>
                            <td style={tdStyle}>{h.target_name || `User #${h.target_user_id}`}</td>
                            <td style={tdStyle}>
                              <code style={{ fontSize: 11, background: '#f3f4f6', padding: '1px 4px', borderRadius: 3 }}>
                                {h.permission}
                              </code>
                            </td>
                            <td style={tdStyle}>
                              <span style={{
                                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                                background: h.action.includes('GRANT') ? '#f0fdf4' : h.action.includes('DENY') ? '#fef2f2' : '#f9fafb',
                                color: h.action.includes('GRANT') ? '#166534' : h.action.includes('DENY') ? '#991b1b' : '#374151',
                              }}>
                                {h.action}
                              </span>
                            </td>
                            <td style={tdStyle}>
                              <span style={{ color: '#9ca3af' }}>{h.old_value || '—'}</span>
                              {' → '}
                              <span style={{ fontWeight: 600 }}>{h.new_value || '—'}</span>
                            </td>
                            <td style={tdStyle}>{h.changed_by_name || `#${h.changed_by}`}</td>
                            <td style={{ ...tdStyle, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {h.reason || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}

// ── Shared styles ────────────────────────────────────────────────────────────
function btnStyle(color) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '7px 14px', borderRadius: 8, border: 'none',
    background: color, color: '#fff', fontSize: 12, fontWeight: 600,
    cursor: 'pointer',
  };
}

function actionBtn(color, isActive) {
  return {
    width: 28, height: 28, borderRadius: 6, border: `1px solid ${isActive ? color : '#d1d5db'}`,
    background: isActive ? `${color}15` : '#fff', color: isActive ? color : '#9ca3af',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: isActive ? 'default' : 'pointer', opacity: isActive ? 0.6 : 1,
  };
}

const thStyle = {
  textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 600,
  color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em',
};
const tdStyle = { padding: '8px 12px' };

const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 600, color: '#374151',
  marginBottom: 4,
};
const inputStyle = {
  padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db',
  fontSize: 13, boxSizing: 'border-box',
};
const btnSmall = {
  padding: '3px 10px', borderRadius: 5, border: '1px solid #d1d5db',
  background: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer',
  color: '#6b7280',
};
